import fs from 'node:fs'
import path from 'node:path'
import { writeConfigFile } from '@core-engine/config/serialize.js'
import type { ResolvedBetterstartConfig } from '@core-engine/config/types.js'
import {
  type LoadedSchema,
  listSchemaNames,
  loadSchema,
  validateLoadedSchema
} from '@core-engine/schema/schema-reader.js'
import { ensureSnapshotGitFiles } from '@core-engine/snapshots/gitignore.js'
import {
  BARREL_SCOPE,
  deleteSnapshot,
  hasTombstone,
  loadManifest
} from '@core-engine/snapshots/store.js'
import { appendEnvVars, removeEnvVars } from '@core-engine/utils/env.js'
import type { PackageManager } from '@core-engine/utils/package-manager.js'
import {
  applyAdminNamespaceToContent,
  applyAdminNamespaceToPath
} from '@next-adapter/config/namespace.js'
import { resolveProjectPaths } from '@next-adapter/config/paths.js'
import { runFormPipeline } from '@next-adapter/generators/form-pipeline/index.js'
import { runEntityPipeline, runSinglePipeline } from '@next-adapter/generators/index.js'
import {
  getDependencyPlan,
  installDependenciesAsync,
  removeDependenciesAsync
} from '@next-adapter/init/scaffolders/dependencies.js'
import { getIntegrationDefinition } from '@next-adapter/integration-registry.js'
import {
  formatUnknownPresetMessage,
  getPresetDefinition,
  isPresetId,
  listPresetDefinitions
} from '@next-adapter/preset-registry.js'
import { readPresetTemplate } from '@next-adapter/preset-template-reader.js'
import { applyGeneratedFiles } from '@next-adapter/snapshots/apply.js'
import { renderBarrelFiles } from '@next-adapter/snapshots/barrels.js'
import { readTemplate } from '@next-adapter/template-reader.js'
import {
  deleteInstalledPresetManifest,
  loadInstalledPresetManifest,
  saveInstalledPresetManifest
} from '@preset-engine/manifests.js'
import {
  getSchemaOwner,
  loadSchemaOwnershipIndex,
  removeSchemaOwner,
  setSchemaOwner
} from '@preset-engine/ownership.js'
import type {
  BetterstartPresetDefinition,
  BetterstartPresetId,
  InstalledPresetManifest
} from '@preset-engine/types.js'

interface ConfigurePresetResult {
  configured: boolean
  addedEnvKeys: string[]
  updatedEnvKeys: string[]
}

export interface InstallPresetsOptions {
  cwd: string
  config: ResolvedBetterstartConfig
  pm: PackageManager
  presetIds: BetterstartPresetId[]
  interactive: boolean
  includeBiome: boolean
}

export interface InstallPresetsResult {
  installed: BetterstartPresetId[]
  skipped: BetterstartPresetId[]
  warnings: string[]
  config: ResolvedBetterstartConfig
}

export interface RemovePresetsOptions {
  cwd: string
  config: ResolvedBetterstartConfig
  pm: PackageManager
  presetIds: BetterstartPresetId[]
}

export interface RemovePresetsResult {
  removed: BetterstartPresetId[]
  warnings: string[]
  config: ResolvedBetterstartConfig
}

function unique<T>(values: T[]): T[] {
  return Array.from(new Set(values))
}

function normalizeInstalledPresets(config: ResolvedBetterstartConfig): BetterstartPresetId[] {
  return unique((config.presets.installed ?? []).filter(isPresetId)).sort()
}

function resolvePresetInstallOrder(presetIds: BetterstartPresetId[]): BetterstartPresetId[] {
  const ordered: BetterstartPresetId[] = []
  const visiting = new Set<BetterstartPresetId>()
  const visited = new Set<BetterstartPresetId>()

  function visit(presetId: BetterstartPresetId): void {
    if (visited.has(presetId)) {
      return
    }
    if (visiting.has(presetId)) {
      throw new Error(`Circular preset dependency detected at "${presetId}".`)
    }

    visiting.add(presetId)
    for (const dependency of getPresetDefinition(presetId).dependencies) {
      visit(dependency)
    }
    visiting.delete(presetId)
    visited.add(presetId)
    ordered.push(presetId)
  }

  for (const presetId of presetIds) {
    visit(presetId)
  }

  return ordered
}

function getSchemasDir(cwd: string, config: ResolvedBetterstartConfig): string {
  const paths = resolveProjectPaths(config)
  return path.join(cwd, ...paths.schemasDir.split('/'))
}

function runLoadedPipeline(
  loaded: LoadedSchema,
  cwd: string,
  config: ResolvedBetterstartConfig,
  outputNamespace?: string
) {
  if (loaded.type === 'form') {
    return runFormPipeline(loaded.schema, cwd, config, {
      force: false,
      silent: true,
      outputNamespace
    })
  }

  if (loaded.type === 'single') {
    return runSinglePipeline(loaded.schema, cwd, config, {
      force: false,
      silent: true,
      outputNamespace
    })
  }

  return runEntityPipeline(loaded.schema, cwd, config, {
    force: false,
    silent: true,
    outputNamespace
  })
}

async function applyGeneratedSchema(
  cwd: string,
  config: ResolvedBetterstartConfig,
  loaded: LoadedSchema,
  outputNamespace?: string
): Promise<void> {
  const validationErrors = validateLoadedSchema(loaded)
  if (validationErrors.length > 0) {
    throw new Error(validationErrors.join('; '))
  }

  const pipelineResult = runLoadedPipeline(loaded, cwd, config, outputNamespace)
  if (!pipelineResult.success) {
    throw new Error(pipelineResult.errors.join('; '))
  }

  await applyGeneratedFiles({
    cwd,
    config,
    scope: loaded.schema.name,
    schemaJson: loaded.schema,
    generatedFiles: pipelineResult.files,
    force: false,
    interactive: false
  })
}

async function regenerateBarrels(cwd: string, config: ResolvedBetterstartConfig): Promise<void> {
  await applyGeneratedFiles({
    cwd,
    config,
    scope: BARREL_SCOPE,
    schemaJson: { name: BARREL_SCOPE },
    generatedFiles: renderBarrelFiles(cwd, config),
    force: false,
    interactive: false
  })
}

function resolvePresetProjectPath(config: ResolvedBetterstartConfig, outputPath: string): string {
  const namespacedOutputPath = applyAdminNamespaceToPath(
    outputPath,
    config.frameworkConfig.next.namespace
  )
  return path.posix.join(config.paths.admin.replace(/^\.\//, ''), namespacedOutputPath)
}

function namespacePresetContent(config: ResolvedBetterstartConfig, content: string): string {
  return applyAdminNamespaceToContent(content, config.frameworkConfig.next.namespace)
}

function flattenEnvKeys(definition: BetterstartPresetDefinition): string[] {
  return definition.envSections.flatMap((section) => section.vars.map((entry) => entry.key))
}

function normalizeManagedFileContent(content: string): string {
  return content.replace(/\r\n/g, '\n')
}

function tryReadCoreManagedTemplate(relativeOutputPath: string): string | null {
  try {
    return readTemplate(relativeOutputPath)
  } catch {
    return null
  }
}

function configurePreset(
  definition: BetterstartPresetDefinition,
  cwd: string
): ConfigurePresetResult {
  const envResult = appendEnvVars(cwd, definition.envSections)
  return {
    configured: true,
    addedEnvKeys: envResult.added,
    updatedEnvKeys: envResult.updated
  }
}

function ensurePresetSchemaTargetsAvailable(
  cwd: string,
  config: ResolvedBetterstartConfig,
  definition: BetterstartPresetDefinition
): void {
  const schemasDir = getSchemasDir(cwd, config)

  for (const schemaFile of definition.schemaFiles) {
    const schemaName = schemaFile.replace(/\.json$/, '')
    const destinationPath = path.join(schemasDir, schemaFile)
    if (!fs.existsSync(destinationPath)) {
      continue
    }

    const owner = getSchemaOwner(cwd, schemaName)
    if (owner && owner !== `preset:${definition.id}`) {
      throw new Error(
        `Schema "${schemaName}" is already owned by "${owner}". Remove or rename it before installing ${definition.id}.`
      )
    }

    if (!owner) {
      throw new Error(
        `Schema "${schemaName}" already exists and is not preset-owned. Remove or rename it before installing ${definition.id}.`
      )
    }
  }
}

function ensurePresetDirectFileTargetsAvailable(
  cwd: string,
  config: ResolvedBetterstartConfig,
  definition: BetterstartPresetDefinition
): void {
  for (const file of definition.files) {
    const projectRelativePath = resolvePresetProjectPath(config, file.outputPath)
    const fullPath = path.join(cwd, ...projectRelativePath.split('/'))

    if (!fs.existsSync(fullPath)) {
      continue
    }

    const stats = fs.statSync(fullPath)
    if (!stats.isFile()) {
      throw new Error(
        `Installing ${definition.id} would overwrite "${projectRelativePath}", but it is not a file. Remove or rename it before continuing.`
      )
    }

    const existingContent = normalizeManagedFileContent(fs.readFileSync(fullPath, 'utf-8'))
    const presetTemplate = normalizeManagedFileContent(
      namespacePresetContent(config, readPresetTemplate(definition.id, file.templatePath))
    )

    if (existingContent === presetTemplate) {
      continue
    }

    const coreTemplate = tryReadCoreManagedTemplate(file.outputPath)
    if (
      coreTemplate &&
      existingContent === normalizeManagedFileContent(namespacePresetContent(config, coreTemplate))
    ) {
      continue
    }

    throw new Error(
      `Installing ${definition.id} would overwrite existing file "${projectRelativePath}". Rename, remove, or reset it to the managed template before installing ${definition.id}.`
    )
  }
}

function writePresetDirectFiles(
  cwd: string,
  config: ResolvedBetterstartConfig,
  definition: BetterstartPresetDefinition
): string[] {
  const writtenFiles: string[] = []

  for (const file of definition.files) {
    const projectRelativePath = resolvePresetProjectPath(config, file.outputPath)
    const fullPath = path.join(cwd, ...projectRelativePath.split('/'))
    fs.mkdirSync(path.dirname(fullPath), { recursive: true })
    fs.writeFileSync(
      fullPath,
      namespacePresetContent(config, readPresetTemplate(definition.id, file.templatePath)),
      'utf-8'
    )
    writtenFiles.push(projectRelativePath)
  }

  return writtenFiles
}

function toProjectAbsolutePath(cwd: string, filePath: string): string {
  return path.isAbsolute(filePath) ? filePath : path.join(cwd, ...filePath.split('/'))
}

function isInsideDir(child: string, parent: string): boolean {
  const relative = path.relative(parent, child)
  return relative === '' || (!relative.startsWith('..') && !path.isAbsolute(relative))
}

function cleanupEmptyDirs(
  cwd: string,
  deletedPaths: string[],
  config: ResolvedBetterstartConfig
): void {
  const paths = resolveProjectPaths(config)
  const projectRoot = path.resolve(cwd)
  const stopRoots = new Set([
    projectRoot,
    toProjectAbsolutePath(cwd, paths.adminDir),
    toProjectAbsolutePath(cwd, paths.pagesDir),
    toProjectAbsolutePath(cwd, paths.schemasDir),
    path.join(projectRoot, '.betterstart'),
    path.join(projectRoot, '.betterstart', 'presets'),
    path.join(projectRoot, '.betterstart', 'snapshots')
  ])

  for (const deletedPath of deletedPaths) {
    let currentDir = path.dirname(toProjectAbsolutePath(cwd, deletedPath))

    while (isInsideDir(currentDir, projectRoot) && !stopRoots.has(currentDir)) {
      if (!fs.existsSync(currentDir)) {
        currentDir = path.dirname(currentDir)
        continue
      }

      if (fs.readdirSync(currentDir).length > 0) {
        break
      }

      fs.rmdirSync(currentDir)
      currentDir = path.dirname(currentDir)
    }
  }
}

function restoreOrDeletePresetFile(
  cwd: string,
  config: ResolvedBetterstartConfig,
  relativeOutputPath: string
): string | null {
  const projectRelativePath = resolvePresetProjectPath(config, relativeOutputPath)
  const fullPath = toProjectAbsolutePath(cwd, projectRelativePath)

  try {
    const coreTemplate = readTemplate(relativeOutputPath)
    fs.mkdirSync(path.dirname(fullPath), { recursive: true })
    fs.writeFileSync(fullPath, namespacePresetContent(config, coreTemplate), 'utf-8')
    return null
  } catch {
    const existed = fs.existsSync(fullPath)
    fs.rmSync(fullPath, { force: true })
    return existed ? projectRelativePath : null
  }
}

function writePresetSchemas(
  cwd: string,
  config: ResolvedBetterstartConfig,
  definition: BetterstartPresetDefinition
): string[] {
  const schemasDir = getSchemasDir(cwd, config)
  const writtenSchemaNames: string[] = []

  for (const schemaFile of definition.schemaFiles) {
    const outputPath = path.join(schemasDir, schemaFile)
    fs.mkdirSync(path.dirname(outputPath), { recursive: true })
    fs.writeFileSync(
      outputPath,
      readPresetTemplate(definition.id, `schemas/${schemaFile}`),
      'utf-8'
    )
    writtenSchemaNames.push(schemaFile.replace(/\.json$/, ''))
  }

  return writtenSchemaNames
}

function removePath(cwd: string, filePath: string): boolean {
  const fullPath = toProjectAbsolutePath(cwd, filePath)
  const existed = fs.existsSync(fullPath)
  fs.rmSync(fullPath, { recursive: true, force: true })
  return existed
}

function removePresetSchemas(
  cwd: string,
  config: ResolvedBetterstartConfig,
  manifest: InstalledPresetManifest
): string[] {
  const paths = resolveProjectPaths(config)
  const deletedPaths: string[] = []
  const manifestsBySchema = new Map<string, NonNullable<ReturnType<typeof loadManifest>>>()
  const missingManifests: string[] = []

  for (const schemaName of manifest.ownedSchemas) {
    const scopeManifest = loadManifest(cwd, schemaName)
    if (scopeManifest) {
      manifestsBySchema.set(schemaName, scopeManifest)
    } else {
      missingManifests.push(schemaName)
    }
  }

  if (missingManifests.length > 0) {
    throw new Error(
      `Cannot remove ${manifest.id}. Missing snapshot manifest${
        missingManifests.length === 1 ? '' : 's'
      } for preset-owned schema${missingManifests.length === 1 ? '' : 's'}: ${missingManifests.join(
        ', '
      )}. Re-run \`pnpm betterstart init --force\` to restore snapshot state before removing the preset.`
    )
  }

  for (const schemaName of manifest.ownedSchemas) {
    const scopeManifest = manifestsBySchema.get(schemaName)
    if (scopeManifest) {
      for (const filePath of [
        ...scopeManifest.files.map((entry) => entry.path),
        ...scopeManifest.skipped
      ]) {
        if (removePath(cwd, filePath)) {
          deletedPaths.push(filePath)
        }
      }
      deleteSnapshot(cwd, schemaName)
    }

    const schemaPath = path.posix.join(paths.schemasDir, `${schemaName}.json`)
    const formsSchemaPath = path.posix.join(paths.schemasDir, 'forms', `${schemaName}.json`)
    if (removePath(cwd, schemaPath)) {
      deletedPaths.push(schemaPath)
    }
    if (removePath(cwd, formsSchemaPath)) {
      deletedPaths.push(formsSchemaPath)
    }
    if (hasTombstone(cwd, schemaName)) {
      fs.rmSync(path.join(cwd, '.betterstart', 'snapshots', '_removed', schemaName), {
        force: true
      })
    }
    removeSchemaOwner(cwd, schemaName)
  }

  return deletedPaths
}

function collectSchemaRelationshipTargets(loaded: LoadedSchema): string[] {
  const targets = new Set<string>()

  function walkFields(fields: unknown): void {
    if (!Array.isArray(fields)) return

    for (const field of fields) {
      if (!field || typeof field !== 'object') {
        continue
      }

      const fieldRecord = field as Record<string, unknown>
      const relationship =
        typeof fieldRecord.relationship === 'string' ? fieldRecord.relationship : undefined
      if (relationship) {
        targets.add(relationship)
      }

      walkFields(fieldRecord.fields)

      if (Array.isArray(fieldRecord.tabs)) {
        for (const tab of fieldRecord.tabs) {
          if (!tab || typeof tab !== 'object') {
            continue
          }
          walkFields((tab as Record<string, unknown>).fields)
        }
      }
    }
  }

  if (loaded.type === 'form') {
    walkFields(loaded.schema.fields)
    if (Array.isArray(loaded.schema.steps)) {
      for (const step of loaded.schema.steps) {
        walkFields(step.fields)
      }
    }
  } else {
    walkFields(loaded.schema.fields)
  }

  return Array.from(targets)
}

function findBlockingSchemaDependencies(
  cwd: string,
  config: ResolvedBetterstartConfig,
  ownedSchemas: string[]
): string[] {
  const blockers: string[] = []
  const schemasDir = getSchemasDir(cwd, config)
  const ownership = loadSchemaOwnershipIndex(cwd)

  for (const schemaName of listSchemaNames(schemasDir)) {
    if (ownership.schemas[schemaName] !== 'user') {
      continue
    }

    const loaded = loadSchema(schemasDir, schemaName)
    const relationshipTargets = collectSchemaRelationshipTargets(loaded).filter((target) =>
      ownedSchemas.includes(target)
    )

    if (relationshipTargets.length > 0) {
      blockers.push(
        `${schemaName} references preset-owned schema(s): ${relationshipTargets.join(', ')}`
      )
    }
  }

  return blockers
}

function getUnusedPresetDependencies(
  remainingPresets: BetterstartPresetId[],
  removedPresetId: BetterstartPresetId,
  installedIntegrations: string[]
): { dependencies: string[]; devDependencies: string[] } {
  const remainingDefinitions = remainingPresets.map((presetId) => getPresetDefinition(presetId))
  const integrationDefinitions = installedIntegrations.map((integrationId) =>
    getIntegrationDefinition(integrationId)
  )
  const remainingDependencies = new Set([
    ...remainingDefinitions.flatMap((definition) => definition.packageDependencies),
    ...integrationDefinitions.flatMap((definition) => definition.packageDependencies)
  ])
  const remainingDevDependencies = new Set([
    ...remainingDefinitions.flatMap((definition) => definition.devDependencies),
    ...integrationDefinitions.flatMap((definition) => definition.devDependencies)
  ])

  const corePlan = getDependencyPlan([], [], false)
  const removedDefinition = getPresetDefinition(removedPresetId)

  return {
    dependencies: removedDefinition.packageDependencies.filter(
      (dependency) =>
        !remainingDependencies.has(dependency) && !corePlan.dependencies.includes(dependency)
    ),
    devDependencies: removedDefinition.devDependencies.filter(
      (dependency) =>
        !remainingDevDependencies.has(dependency) && !corePlan.devDependencies.includes(dependency)
    )
  }
}

export function parsePresetList(value: string | undefined): BetterstartPresetId[] {
  if (!value) {
    return []
  }

  const presetIds = value
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)

  const invalid = presetIds.filter((presetId) => !isPresetId(presetId))
  if (invalid.length > 0) {
    throw new Error(formatUnknownPresetMessage(invalid))
  }

  return unique(presetIds as BetterstartPresetId[])
}

export function getInstalledPresets(config: ResolvedBetterstartConfig): BetterstartPresetId[] {
  return normalizeInstalledPresets(config)
}

export function syncInstalledPresetManifests(cwd: string, config: ResolvedBetterstartConfig): void {
  for (const presetId of normalizeInstalledPresets(config)) {
    const manifest = loadInstalledPresetManifest(cwd, presetId)
    if (!manifest) {
      continue
    }

    const definition = getPresetDefinition(presetId)
    const nextManifest: InstalledPresetManifest = {
      ...manifest,
      version: definition.version,
      kind: definition.kind,
      ownedFiles: definition.files.map((file) => resolvePresetProjectPath(config, file.outputPath)),
      packageDependencies: definition.packageDependencies,
      devDependencies: definition.devDependencies,
      dependencies: definition.dependencies,
      conflicts: definition.conflicts
    }

    if (JSON.stringify(nextManifest) !== JSON.stringify(manifest)) {
      saveInstalledPresetManifest(cwd, nextManifest)
    }
  }
}

export function ensureCoreSchemaOwnership(cwd: string, schemaName: string): void {
  setSchemaOwner(cwd, schemaName, 'core')
}

export function ensureUserSchemaOwnership(cwd: string, schemaName: string): void {
  if (schemaName === 'settings') {
    setSchemaOwner(cwd, schemaName, 'core')
    return
  }

  const owner = getSchemaOwner(cwd, schemaName)
  if (!owner) {
    setSchemaOwner(cwd, schemaName, 'user')
  }
}

export async function installPresets({
  cwd,
  config,
  pm,
  presetIds,
  includeBiome
}: InstallPresetsOptions): Promise<InstallPresetsResult> {
  const orderedPresetIds = resolvePresetInstallOrder(presetIds)
  const installedPresetIds = normalizeInstalledPresets(config)
  const skipped: BetterstartPresetId[] = []
  const installed: BetterstartPresetId[] = []
  const warnings: string[] = []

  ensureSnapshotGitFiles(cwd)

  for (const presetId of orderedPresetIds) {
    if (installedPresetIds.includes(presetId)) {
      skipped.push(presetId)
      continue
    }

    const definition = getPresetDefinition(presetId)
    ensurePresetSchemaTargetsAvailable(cwd, config, definition)
    ensurePresetDirectFileTargetsAvailable(cwd, config, definition)

    const configureResult = configurePreset(definition, cwd)
    const dependencyPlan = getDependencyPlan(
      [...installedPresetIds, presetId],
      config.integrations.installed,
      includeBiome
    )
    const currentPlan = getDependencyPlan(
      installedPresetIds,
      config.integrations.installed,
      includeBiome
    )

    const dependencyDiff = {
      dependencies: dependencyPlan.dependencies.filter(
        (dependency) => !currentPlan.dependencies.includes(dependency)
      ),
      devDependencies: dependencyPlan.devDependencies.filter(
        (dependency) => !currentPlan.devDependencies.includes(dependency)
      )
    }

    if (dependencyDiff.dependencies.length > 0 || dependencyDiff.devDependencies.length > 0) {
      const result = await installDependenciesAsync({
        cwd,
        pm,
        dependencies: dependencyDiff.dependencies,
        devDependencies: dependencyDiff.devDependencies
      })

      if (!result.success) {
        throw new Error(result.error ?? `Failed to install dependencies for ${presetId}.`)
      }
    }

    const writtenFiles = writePresetDirectFiles(cwd, config, definition)
    const writtenSchemas = writePresetSchemas(cwd, config, definition)

    for (const schemaName of writtenSchemas) {
      const loaded = loadSchema(getSchemasDir(cwd, config), schemaName)
      await applyGeneratedSchema(cwd, config, loaded, presetId)
      setSchemaOwner(cwd, schemaName, `preset:${presetId}`)
    }

    if (writtenSchemas.length > 0) {
      await regenerateBarrels(cwd, config)
    }

    installedPresetIds.push(presetId)
    config.presets.installed = unique(installedPresetIds).sort()

    const manifest: InstalledPresetManifest = {
      id: presetId,
      version: definition.version,
      kind: definition.kind,
      ownedSchemas: writtenSchemas,
      ownedFiles: writtenFiles,
      envKeys: flattenEnvKeys(definition),
      packageDependencies: definition.packageDependencies,
      devDependencies: definition.devDependencies,
      configured: configureResult.configured,
      dependencies: definition.dependencies,
      conflicts: definition.conflicts,
      installedAt: new Date().toISOString()
    }

    saveInstalledPresetManifest(cwd, manifest)
    writeConfigFile(cwd, config)
    installed.push(presetId)
  }

  return {
    installed,
    skipped,
    warnings,
    config
  }
}

export async function removePresets({
  cwd,
  config,
  pm,
  presetIds
}: RemovePresetsOptions): Promise<RemovePresetsResult> {
  const installedPresets = normalizeInstalledPresets(config)
  const removed: BetterstartPresetId[] = []
  const warnings: string[] = []

  for (const presetId of presetIds) {
    if (!installedPresets.includes(presetId)) {
      warnings.push(`${presetId} is not installed.`)
      continue
    }

    const manifest = loadInstalledPresetManifest(cwd, presetId)
    if (!manifest) {
      throw new Error(`Missing manifest for installed preset "${presetId}".`)
    }

    const dependents = installedPresets.filter((installedPresetId) => {
      if (installedPresetId === presetId) {
        return false
      }

      return getPresetDefinition(installedPresetId).dependencies.includes(presetId)
    })

    if (dependents.length > 0) {
      throw new Error(
        `Cannot remove ${presetId}. The following presets depend on it: ${dependents.join(', ')}`
      )
    }

    const blockers = findBlockingSchemaDependencies(cwd, config, manifest.ownedSchemas)
    if (blockers.length > 0) {
      throw new Error(`Cannot remove ${presetId}:\n- ${blockers.join('\n- ')}`)
    }

    const deletedPaths = removePresetSchemas(cwd, config, manifest)

    for (const filePath of manifest.ownedFiles) {
      const relativeOutputPath = filePath.replace(`${config.paths.admin.replace(/^\.\//, '')}/`, '')
      const deletedPath = restoreOrDeletePresetFile(cwd, config, relativeOutputPath)
      if (deletedPath) {
        deletedPaths.push(deletedPath)
      }
    }

    deleteInstalledPresetManifest(cwd, presetId)
    const nextInstalledPresets = installedPresets.filter(
      (installedPresetId) => installedPresetId !== presetId
    )
    installedPresets.splice(0, installedPresets.length, ...nextInstalledPresets)
    config.presets.installed = [...nextInstalledPresets].sort()

    const remainingPresets = normalizeInstalledPresets(config)
    const dependencyDiff = getUnusedPresetDependencies(
      remainingPresets,
      presetId,
      config.integrations.installed
    )

    if (dependencyDiff.dependencies.length > 0 || dependencyDiff.devDependencies.length > 0) {
      const result = await removeDependenciesAsync({
        cwd,
        pm,
        dependencies: dependencyDiff.dependencies,
        devDependencies: dependencyDiff.devDependencies
      })

      if (!result.success) {
        warnings.push(result.error ?? `Failed to remove dependencies for ${presetId}.`)
      }
    }

    const usedEnvKeys = new Set(
      remainingPresets.flatMap((installedPresetId) =>
        flattenEnvKeys(getPresetDefinition(installedPresetId))
      )
    )
    const removableEnvKeys = manifest.envKeys.filter((envKey) => !usedEnvKeys.has(envKey))
    if (removableEnvKeys.length > 0) {
      removeEnvVars(cwd, removableEnvKeys)
    }

    if (manifest.ownedSchemas.length > 0) {
      await regenerateBarrels(cwd, config)
    }

    cleanupEmptyDirs(cwd, deletedPaths, config)

    writeConfigFile(cwd, config)
    removed.push(presetId)
  }

  return {
    removed,
    warnings,
    config
  }
}

export function listAvailablePresets() {
  return listPresetDefinitions()
}
